<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useColor } from '@/composables/color';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import CButton from '@/components/shared/CButton';
import CSlider from '@/components/shared/CSlider.vue';
import CInput from '@/components/shared/CInput.vue';
import CFileInput from '@/components/shared/CFileInput';
import CSelect, { type Primitive, type CSelectItem, valueReducer } from '@/components/shared/CSelect';
import type { CanvasEx } from '@/typings/canvas';
import type {
    ActiveSelection,
    BaseTextElement,
    BoundedTextElement,
    FontColorOption,
    PathITextElement,
    TextTexture,
    TextTransformElement
} from '@/typings/customilyElements/canvasElements';
import type { ColorObject, GradientObject, LibraryDetailElement, PaletteObject } from '@/typings/libraries';
import type { ElementMapOption } from './types';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { UserTextTexture } from '@/api/typings/product';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { getLibraryElementDetails } from '@/api/libraries';
import { runTasksInChunks } from '@/utils/promiseUtils';

const DEFAULT_STROKE_COLOR = '#000000';
const INITIAL_STROKE_COLOR = '#FF0054';
const KEY_MAP = {
  outline: {
    strokeOption: 'outlineColorOption',
    strokeColorsMap: 'outlineColorsMap',
    strokeLibraryId: 'outlineColorLibraryId',
    strokeColor: 'outlineColor',
    type: 'outline',
  },
  stroke: {
    strokeOption: 'strokeColorOption',
    strokeColorsMap: 'strokeColorsMap',
    strokeLibraryId: 'strokeColorLibraryId',
    strokeColor: 'strokeColor',
    type: 'stroke',
  },
} as const
const COLOR_TYPE = LibraryTypeEnum.Color;
type TextElement = BoundedTextElement | TextTransformElement | PathITextElement;
type COLOR_MAP = 'fontColorsMap' | 'outlineColorsMap' | 'strokeColorsMap';
type LIBRARY_MAP = 'colorLibraryId' | 'outlineColorLibraryId' | 'strokeColorLibraryId';

const props = withDefaults(
    defineProps<{
        canvas: CanvasEx;
        textElement?: TextElement;
        editMode?: boolean;
        isTextureDisabled?: boolean;
        textureList?: UserTextTexture[];
        cmykMode?: boolean;
        forTypes?: string[];
        allowTextureUpload?: boolean;
        selectedGroup?: ActiveSelection<BaseTextElement>
        shadowApplied?: boolean;
    }>(),
    {
        allowTextureUpload: true,
        textureList: () => []
    }
);

const emit = defineEmits<{
    (e: 'open-color-library-selector'): void;
    (e: 'open-outline-color-library-selector'): void;
    (e: 'open-stroke-color-library-selector'): void;
    (e: 'texture-selected', path?: string): void;
    (e: 'texture-upload', file: File): void;
    (e: 'texture-delete', path: string): void;
}>();

const type = ref({
    text: true,
    warpText: false,
    circularText: false,
    iText: false
});

const showMenuTypes = computed(() => {
    // This is a temporary solution to disable gradients for text elements
    // until we have a proper design for gradients in text elements.
    if (props.isTextureDisabled) return false;
    if (type.value.circularText || type.value.iText) return false;
    return true;
});

const isPaletteEnabled = computed(() => type.value.text);
const isStrokeEnabled = ref(false);

const disabled = ref(false);
const model = reactive({
    /* outline */
    outlineWidth: 0 as number | undefined,
    outlineColorOption: undefined as number | undefined,
    outlineColorsMap: new Map<number, FontColorOption>(),
    outlineColorLibraryId: undefined as number | undefined,
    /* fill */
    colorLibraryId: undefined as number | undefined,
    fontColorOption: undefined as number | undefined,
    fontColorsMap: new Map<number, FontColorOption>(),
    textures: [] as TextTexture[],
    texture: '',
    textureScale: 0,
    isFontColorsMixed: false,
    isColorLibraryIdMixed: false,
    isOutlineColorsMixed: false,
    isOutlineColorLibraryIdMixed: false,
});

const colord = useColor();

watch(
    () => props.textElement,
    (element) => {
        if (!element || (props.forTypes?.length && !props.forTypes.includes(element.type))) return;

        type.value = {
            text: false,
            circularText: false,
            warpText: false,
            iText: false
        };

        disabled.value = element.locked;

        model.fontColorOption = element.fontColorOption;
        model.colorLibraryId = element.colorLibraryId;
        model.fontColorsMap = new Map(element.fontColorsMap);

        /**
         * For old templates or new templates cloned from the old ones,
         * the font color could be saved as a hex string instead of the standard color object
         */
        if (typeof element.color == 'string' && colord.isValidColor(element.color)) {
            element.color = transformHexToColorObj(element.color);
            transformFontColorMap(element as BaseTextElement);
        }

        if (element.type == 'text-transform') {
            type.value.warpText = true;
        }

        model.outlineWidth = element.getOutlineWidth?.() || 0;
        model.outlineColorOption = element.outlineColorOption;
        model.outlineColorLibraryId = element.outlineColorLibraryId;
        model.outlineColorsMap = new Map(element.outlineColorsMap);

        switch (element.type) {
            case 'bounded-text':
                type.value.text = true;
                model.textures = element.textures || [];
                model.texture = element.texture;
                model.textureScale = element.textureScale == undefined ? 100 : element.textureScale;
                if (element?.Itext) {
                    type.value.iText = true;
                }
                break;
            case 'path-itext':
                type.value.circularText = true;
                model.outlineWidth = element.strokeWidth;
                if (model.outlineColorsMap.size == 0 && !model.outlineColorLibraryId) {
                    // element.outlineColor in this case could be a rgba or hex value
                    const color = transformHexToColorObj(element.outlineColor);
                    element.outlineColorsMap.set(1, color);
                    model.outlineColorsMap = new Map(element.outlineColorsMap);
                }
                break;
            case 'text-transform':
                // Initialize stroke properties
                model.outlineWidth = element.getStrokeWidth() || 0;
                model.outlineColorOption = element.strokeColorOption;
                model.outlineColorLibraryId = element.strokeColorLibraryId;
                model.outlineColorsMap = new Map(element.strokeColorsMap);
                break;
        }
        isStrokeEnabled.value = !!model.outlineWidth;
    },
    { immediate: true }
);

const isColorMapMixed = (
    firstMap: BaseTextElement['fontColorsMap'],
    secondMap: BaseTextElement['fontColorsMap']
): boolean => {
    if (firstMap.size !== secondMap.size) return true;
    return [...firstMap.entries()].some(([key, value]) => {
        const mapValue = secondMap.get(key);
        return mapValue && normalizeColorMap(value) !== normalizeColorMap(mapValue);
    });
};

const normalizeColorMap = (color: FontColorOption) => {
    if ('hex' in color)
        return `hex:${color.hex}`;

    if ('angle' in color && 'type' in color && Array.isArray(color.colorStops)) {
        const stops = color.colorStops
            .map((cs) => cs.hex)
            .sort()
            .join(',');
        const offset = color.colorStops
            .map((cs) => cs.offset)
            .sort()
            .join(',');

        return `gradient:angle=${color.angle};type=${color.type};stops=${stops};offsets=${offset}`;
    }

    throw new Error('Object not recognized');
};

const getElementStroke = (element: BaseTextElement): number | undefined =>
    element.getOutlineWidth?.() || element.getStrokeWidth?.() || element.strokeWidth

watch(
    () => props.selectedGroup,
    (group) => {
        const objects = group?.getObjects();
        if (!objects?.length) return;

        const firstElement = objects[0];
        model.fontColorOption = firstElement.fontColorOption || 1;
        model.outlineColorOption = firstElement.outlineColorOption || 1;
        model.outlineWidth = getElementStroke(firstElement);
        for (const element of objects) {
            if (model.isColorLibraryIdMixed &&
                model.isFontColorsMixed &&
                model.isOutlineColorsMixed &&
                model.isOutlineColorLibraryIdMixed
            ) break;

            if (element.colorLibraryId !== firstElement.colorLibraryId) {
                model.isColorLibraryIdMixed = true;
            }

            if (element.outlineColorLibraryId !== firstElement.outlineColorLibraryId) {
                model.isOutlineColorLibraryIdMixed = true;
            }

            if (!model.isFontColorsMixed) {
                model.isFontColorsMixed = isColorMapMixed(firstElement.fontColorsMap, element.fontColorsMap);
            }

            if (!model.isOutlineColorsMixed) {
                model.isOutlineColorsMixed = isColorMapMixed(firstElement.outlineColorsMap, element.outlineColorsMap);
            }

            if (model.fontColorOption !== element.fontColorOption) {
                model.fontColorOption = undefined;
            }

            if (model.outlineColorOption !== element.outlineColorOption) {
                model.outlineColorOption = undefined;
            }

            if (model.outlineWidth !== getElementStroke(element)) {
                model.outlineWidth = undefined;
            }
        }

        // Initialize color properties
        if (!model.isColorLibraryIdMixed) model.colorLibraryId = firstElement.colorLibraryId;
        if (!model.isFontColorsMixed) model.fontColorsMap = new Map(firstElement.fontColorsMap);

        // Initialize outline color properties
        isStrokeEnabled.value = !!model.outlineWidth || model.isOutlineColorLibraryIdMixed || model.isOutlineColorsMixed;
        if (!model.isOutlineColorLibraryIdMixed) model.outlineColorLibraryId = firstElement.outlineColorLibraryId;
        if (!model.isOutlineColorsMixed) model.outlineColorsMap = new Map(firstElement.outlineColorsMap);
    },
    { immediate: true }
);

const transformHexToColorObj = (color: string): ColorObject => {
    const { cmyk, hex, a } = colord.getColorFromString(color);
    return {
        hex,
        alpha: a,
        cmyk: colord.cmykToDecimalString(cmyk)
    };
};

const transformFontColorMap = (element: BaseTextElement) => {
    const color = model.fontColorsMap.get(1);

    if (typeof color != 'string') return;

    if (!colord.isValidColor(color)) {
        model.fontColorsMap = new Map();
        return;
    }

    const newMap = new Map<number, FontColorOption>();

    model.fontColorsMap.forEach((value, key) => {
        if (typeof value == 'string') {
            newMap.set(key, transformHexToColorObj(value));
            return
        };
        newMap.set(key, value);
    })

    element.fontColorsMap = newMap;
    model.fontColorsMap = new Map(element.fontColorsMap);
};

const updateElement = async (
    callback: (element: BaseTextElement, list?: BaseTextElement[]) => void | Promise<void>
) => {
    const promises: Promise<void>[] = [];
    const objects = props.selectedGroup ? props.selectedGroup.getObjects() : [props.textElement as BaseTextElement];

    for (const element of objects) {
        if (!element) continue;
        promises.push(Promise.resolve(callback(element, objects)));
    }
    await runTasksInChunks(promises);
};

const onColorSelected = async (value: ElementMapOption, option = 1) => {
    const color = value as FontColorOption;
    await updateElement((element) => {
        if (props.editMode) {
            element.setEditedProp('fontColorsMap', 'color');
        }

        if (model.isFontColorsMixed) element.fontColorsMap.clear();

        element.fontColorsMap.set(option, color);
        model.fontColorsMap = new Map(element.fontColorsMap);

        if (option == 1) {
            if (typeof color !== 'string' && 'colorStops' in color) {
                element.color = { ...color.colorStops[0] };
            } else if (typeof color !== 'string' && 'colors' in color) {
                element.color = { ...color.colors[0] };
            } else {
                element.color = { ...color };
            }
        }
        loadColorIntoElement(option, undefined, false, element);
    });
    model.isFontColorsMixed = false;
};

const outlineKeyMap = computed(() => type.value.warpText ? KEY_MAP.stroke : KEY_MAP.outline);
const onOutlineColorSelected = async (value: ElementMapOption, option = 1, clearOptions = false) => {
    const color = value as FontColorOption;
    const { strokeColorsMap, strokeColor } = outlineKeyMap.value;
    await updateElement((element) => {
        if (props.editMode) {
            element.setEditedProp(strokeColorsMap, strokeColor);
        }
        if (model.isOutlineColorsMixed || clearOptions) element[strokeColorsMap].clear();

        element[strokeColorsMap].set(option, color);
        model.outlineColorsMap = new Map(element[strokeColorsMap]);
        if (option == 1) {
            if (typeof color !== 'string' && 'colorStops' in color) {
                element[strokeColor] = color.colorStops[0];
            } else if (typeof color !== 'string' && 'colors' in color) {
                element[strokeColor] = color.colors[0];
            } else {
                element[strokeColor] = color;
            }
        }

        loadColorIntoElement(option, undefined, true, element);
    });
    model.isOutlineColorsMixed = false;
};

const outlineWidthPlaceholder = computed(() => props.selectedGroup ? 'Mixed' : '0')
const updateOutlineWidth = (value: number | string) => {
    const key = type.value.circularText || type.value.warpText ? 'strokeWidth' : 'outlineWidth';
    model.outlineWidth = Number(value);
    updateElement((element) => {
        element.set(key, model.outlineWidth ?? 0);
        if (props.editMode) {
            element.setEditedProp(key);
        }
    })
    props.canvas.requestRenderAll();
};

const cachedLibElements = new Map<string, LibraryDetailElement[]>();
const onLoadOption = (option: number, value?: ElementMapOption, isOutline?: boolean) => {
    updateElement(async (element, list) => {
        let val = value;
        if (!list || list.length < 2) {
            loadColorIntoElement(option, val, isOutline, element);
        }

        const libraryId = isOutline ? element.outlineColorLibraryId : element.colorLibraryId;
        if (libraryId) {
            const id = `${libraryId}-${option}`;
            const libElement =
                cachedLibElements.get(id) ||
                (await getLibraryElementDetails(libraryId, COLOR_TYPE, undefined, '', option));
            if (!libElement.length) return;
            cachedLibElements.set(id, libElement);
            val = {
              hex: libElement[0].hex || '',
              alpha: libElement[0].alpha || 1,
              cmyk: ''
            };
        }

        return loadColorIntoElement(option, val, isOutline, element);
    });
};

const previewColorOption = (color?: ElementMapOption, isOutline?: boolean) => {
    updateElement((element) => {
        loadColorIntoElement(model.fontColorOption || 1, color, isOutline, element);
    });
};

const loadColorIntoElement = (option: number, value?: ElementMapOption, isOutline?: boolean, el?: BaseTextElement) => {
    const element = el || props.textElement as BaseTextElement;
    if (!element) return;

    const { strokeColorsMap, strokeOption } = outlineKeyMap.value;
    let colorMap;
    if (isOutline) {
        colorMap = element[strokeColorsMap];
    } else {
        colorMap = element.fontColorsMap;
    }
    const color = (value as FontColorOption) || colorMap.get(option);

    if (!color) return;

    let objColor: string | GradientObject | PaletteObject;
    if ('colorStops' in color || 'colors' in color) {
        objColor = color;
    } else {
        objColor = color.alpha < 1 ? colord.hexToRgba(color.hex, color.alpha) : color.hex;
    }

    if (isOutline) {
        type.value.warpText ? element.setStrokeColor(objColor) : element.setOutlineColor(objColor);
        element[strokeOption] = model.outlineColorOption = option;
    } else {
        element.setColor(objColor);
        element.fontColorOption = model.fontColorOption = option;
    }

    props.canvas.requestRenderAll();
};

const removeColorOptionFromMap = (position: number, key: COLOR_MAP = 'fontColorsMap') => {
    const isOutline = key != 'fontColorsMap';
    const modelKey = isOutline ? 'outlineColorsMap' : key;
    updateElement((element) => {
        if (props.editMode) {
            const type = isOutline ? outlineKeyMap.value.type : 'color';
            element.setEditedProp(key, type);
        }
        element[key].delete(position);
        model[modelKey].delete(position);
        loadColorIntoElement(1, undefined, isOutline, element);
    });
    model[modelKey] = new Map(model[modelKey]);
};

const onColorOptionsReordered = (
    options: KeyValueDto<number, ElementMapOption>[],
    key: COLOR_MAP = 'fontColorsMap'
) => {
    const colorMap = new Map<number, FontColorOption>();
    const isOutline = key != 'fontColorsMap';
    const modelKey = isOutline ? 'outlineColorsMap' : key;
    const type = isOutline ? outlineKeyMap.value.type : 'color';
    options.forEach(({ value }, index) => {
        const position = index + 1;
        colorMap.set(position, value as FontColorOption);
    });
    updateElement((element) => {
        if (props.editMode) {
            element.setEditedProp(key, type);
        }

        element[key] = colorMap;
        model[modelKey] = new Map(colorMap);
        loadColorIntoElement(1, undefined, isOutline, element);
    });
};

const pickColorLibrary = (outline?: boolean) => {
    const optionKey = outline ? outlineKeyMap.value.strokeOption : 'fontColorOption';
    updateElement((el) => {
        el[optionKey] = model.outlineColorOption = 1;
    });

    if (!outline) {
        model.isColorLibraryIdMixed = false;
        emit('open-color-library-selector');
        return;
    }

    if (type.value.warpText) {
        emit('open-stroke-color-library-selector');
        return;
    }

    emit('open-outline-color-library-selector');
    model.isOutlineColorLibraryIdMixed = false;
};

const removeColorLibrary = (key: LIBRARY_MAP = 'colorLibraryId') => {
    const isOutline = key != 'colorLibraryId';
    updateElement((element) => {
        element[key] = undefined;
        if (props.editMode) {
            element.setEditedProp(key);
        }
        loadColorIntoElement(1, undefined, isOutline, element);
    });

    if (isOutline) {
        model.isOutlineColorLibraryIdMixed = false;
        model.outlineColorLibraryId = undefined;
        return;
    }

    model.isColorLibraryIdMixed = false;
    model.colorLibraryId = undefined;
};

const userTextures = computed(() =>
     props.textureList.map<CSelectItem<string, UserTextTexture>>(texture => ({
        label: texture.Name.replace(/\.[^/.]+$/, ''),
        value: texture.Path,
        deletable: props.allowTextureUpload && !!texture.UserId
    }))
);

const onTextureSelected = (item?: CSelectItem<Primitive> | Primitive) => {
    emit('texture-selected', item as string)
};

const onTextureUpload = (file: File[]) => {
    emit('texture-upload', file[0])
};

const deleteFontTexture = (texturePath: Primitive) => {
    emit('texture-delete', texturePath as string);
}

const changeTextureScale = () => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return;

    element.set('textureScale', model.textureScale);
    const texture = element.textures.find(t => t.texturePath === element.texture);
    if (texture) {
        element.set('textures', [{
            ...texture,
            textureScale: model.textureScale
        }]);
    }
    if (props.editMode) {
        element.setEditedProp('textureScale');
        element.setEditedProp('textures');
    }
    props.canvas.requestRenderAll();
};

const toggleStrokeSection = () => {
    let width = 10;
    let color = INITIAL_STROKE_COLOR;
    isStrokeEnabled.value = !isStrokeEnabled.value;

    if (!isStrokeEnabled.value) {
        width = 0;
        color = DEFAULT_STROKE_COLOR;
    }

    const colorValue = transformHexToColorObj(color);
    onOutlineColorSelected(colorValue, 1, true);
    updateOutlineWidth(width);
};

const currentSlot = computed(() => (!model.isOutlineColorsMixed || model.isOutlineColorLibraryIdMixed ? 'header' : 'actions'));
const isHeaderSlotActive = computed(() => currentSlot.value === 'header');
</script>

<template>
    <div class="text-color-wrapper">
        <section class="color-section">
            <h6>Fill</h6>
            <div class="border-bottom" />
            <element-library-selector
                :disabled="disabled"
                :element-list="model.fontColorsMap"
                :library-id="model.colorLibraryId"
                :selected-group="selectedGroup"
                :library-type="COLOR_TYPE"
                :selected-position="model.fontColorOption"
                :cmyk="cmykMode"
                :show-menu-types="showMenuTypes"
                :enable-palette="isPaletteEnabled"
                :mixed-library="model.isColorLibraryIdMixed"
                :mixed-element-list="model.isFontColorsMixed"
                @pick-library="pickColorLibrary"
                @remove-library="removeColorLibrary"
                @option-selected="onColorSelected"
                @remove-map-option="removeColorOptionFromMap"
                @load-option="onLoadOption"
                @options-reordered="onColorOptionsReordered"
                @preview-selection="previewColorOption"
            />
        </section>
        <section class="color-section">
            <h6>Stroke</h6>
            <div class="border-bottom" />
            <c-button
                v-if="!isStrokeEnabled"
                variant="secondary"
                text="Add Stroke"
                :icon="['far', 'plus']"
                :disabled="disabled"
                @click="toggleStrokeSection"
            />
            <element-library-selector
                v-else
                :disabled="disabled"
                :element-list="model.outlineColorsMap"
                :library-id="model.outlineColorLibraryId"
                :library-type="COLOR_TYPE"
                :selected-position="model.outlineColorOption"
                :cmyk="cmykMode"
                :mixed-library="model.isOutlineColorLibraryIdMixed"
                :mixed-element-list="model.isOutlineColorsMixed"
                @pick-library="pickColorLibrary(true)"
                @remove-library="removeColorLibrary(outlineKeyMap.strokeLibraryId)"
                @option-selected="onOutlineColorSelected"
                @remove-map-option="removeColorOptionFromMap($event, outlineKeyMap.strokeColorsMap)"
                @load-option="(option, value) => onLoadOption(option, value, true)"
                @options-reordered="onColorOptionsReordered($event, outlineKeyMap.strokeColorsMap)"
                @preview-selection="previewColorOption($event, true)"
            >
                <template :slot="currentSlot">
                    <c-input
                        v-model="model.outlineWidth"
                        label="Width"
                        :min="0"
                        :placeholder="outlineWidthPlaceholder"
                        type="number"
                        :disabled="disabled"
                        @input="updateOutlineWidth"
                    />
                </template>
                <template v-if="isHeaderSlotActive" #actions>
                    <c-button
                        v-if="isStrokeEnabled"
                        class="remove-stroke-btn"
                        variant="secondary"
                        text="Remove Stroke"
                        :icon="['far', 'trash']"
                        :disabled="disabled"
                        @click="toggleStrokeSection"
                    />
                </template>
            </element-library-selector>
        </section>
        <section v-if="type.text && !props.selectedGroup" class="color-section">
            <h6>Pattern</h6>
            <div class="border-bottom" />
            <div class="row align-items-center">
                <div class="col-6 d-flex align-items-end pr-0">
                    <c-select
                        class="flex-fill"
                        label="Pattern"
                        placeholder="Select a pattern"
                        clearable
                        searchable
                        filterable
                        :value="model.texture"
                        :disabled="disabled || isTextureDisabled || shadowApplied"
                        :options="userTextures"
                        :model-transform="valueReducer"
                        @input="onTextureSelected"
                        @delete-option="deleteFontTexture($event.value)"
                    />
                    <c-file-input
                        v-if="allowTextureUpload"
                        :value="[]"
                        accept=".png,.jpg,.jpeg"
                        :display-preview="false"
                        :disabled="disabled || isTextureDisabled || shadowApplied"
                        @input="onTextureUpload"
                    >
                        <template #uploadTrigger="{ onFileUpload, disabled: triggerDisabled }">
                            <c-button
                                class="ml-2"
                                variant="secondary"
                                :icon="['far', 'plus']"
                                :disabled="triggerDisabled"
                                @click="onFileUpload"
                            />
                        </template>
                    </c-file-input>
                </div>
                <div class="col-6">
                    <label>Scale: {{ model.textureScale }}%</label>
                    <c-slider
                        v-model="model.textureScale"
                        formatter="{value}%"
                        :max="500"
                        :disabled="disabled || isTextureDisabled || shadowApplied || !model.textures.length"
                        @input="changeTextureScale"
                    />
                </div>
            </div>
        </section>
    </div>
</template>

<style lang="scss" scoped>
.text-color-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    .color-section {
        display: flex;
        flex-direction: column;
        gap: 0.625rem;
        h6 {
            font-size: 0.875rem;
            margin-bottom: 0;
        }
    }
    .element-library-selector-wrapper {
        .remove-stroke-btn {
            flex: 1 1 auto;
            order: 4;
        }
    }
}
</style>
